import { ref, computed, onMounted, getCurrentInstance } from 'vue';
import { useDeviceStore } from '@/stores';
import { useSecurityStore } from '@/stores';
import { useNetworksStore } from '@/stores';
import { useMulticastStore } from '@/stores';
import { useConfigurationStore } from '@/stores';
import { useNeighborStore } from '@/stores';
import i18n from '@/i18n';  // Adjust path if necessary


export function useNetwork() {
  const networksRef = ref();

  const deviceStore = useDeviceStore();
  const securityStore = useSecurityStore();
  const networksStore = useNetworksStore();
  const multicastStore = useMulticastStore();
  const configurationStore = useConfigurationStore();
  const neighborStore = useNeighborStore();

  const instance = getCurrentInstance();

  const portAuthenticationStatus = ref(i18n.global.getLocaleMessage('en')["conversion"]["port_authentication_status"]) || [];
  const vlanListGlobal = computed(() => networksStore.vlanList);
  const manualTrunkGlobal = computed(() => networksStore.switchConfigManualTrunk);
  const modePortListGlobal = computed(() => multicastStore.modePortList);
  const globalConfigurationList = computed(() => configurationStore.globalConfigurationList)
  const $sakura = instance?.appContext.config.globalProperties.$sakura;

  const lagList = computed(() => deviceStore.lagList.filter(obj => obj.changed));
  const radiusPortConfig = computed(() => securityStore.radiusPortConfig);
  const portList = computed(() => networksStore.portList);
  const switchConfigManualTrunk = computed(() => networksStore.switchConfigManualTrunk);
  const neighborList = computed(() => neighborStore.neighborList);
  // const vlanList = computed(() => networksStore.vlanList);

  const calcLagList = computed(() => {
    let arr = vlanListGlobal.value.filter(item => item.vlans.some(obj => obj.static));
    let list = [...lagList.value].map(item => {
      item.tu = item.lagMode === 3 ? 2 : 0;
      item.value = false;
      item.hex = '#000000';
      arr.forEach(obj => {
        let arr1 = obj.vlans.flatMap(nos => (nos.ports?.untagged ?? []));
        if (arr1.includes(item.portNum) && obj.color) {
          item.hex = obj.color;
        }
      });
      return item;
    });

    modePortListGlobal.value?.lagPort?.forEach(item => {
      list = list.map(obj => {
        if (item.portNum === obj.portNum) {
          obj.mode = item.multicastMode === 2 ? ['blockMulticast'] : item.multicastMode === 1 ? ['forceMulticast'] : obj.mode;
        }
        return obj;
      });
    });

    manualTrunkGlobal.value?.lagPort?.forEach(groupId => {
      list.forEach(obj => {
        if (obj.groupId === groupId) {
          obj.mode = [...(obj.mode || []), 'manualTrunk'];
        }
      });
    });
    return list;
  });

  const networkPriority = [
    'block', 'adminDown', 'Authorized', 'Unauthorized', 'lock', 'unlock', 'dynamic', 'warning', 'L', 'autoTrunk', 'manualTrunk',
    'trunk', 'poe', 'poeDisabled', 'blockMulticast', 'forceMulticast', 'S', 'S+', 'S28', 'QS+', 'Q28', 'S56'
  ];

  function loadLag(networksRef) {
    lagList.value.filter(obj => !!(obj.changed)).forEach(item => {
      if (item.members.length) {
        item.members.forEach(key => {
          networksRef.value?.updateIterator(obj => {
            if (obj.portNum === key) {
              obj.mode = ['L'].concat(obj.mode || []);
              obj.lag = item.groupId;
              obj.priority = networkPriority;
            }
            return obj;
          });
        });
      }
    });
  }

  function loadRadius(networksRef) {
    const port_authentication_status = portAuthenticationStatus.value;
    radiusPortConfig.value.forEach(item => {
      networksRef.value?.updateIterator(obj => {
        if (obj.portNum == item.portNum) {
          let text = $sakura.conversionList(port_authentication_status, item.portControlMode, 'label', 'value', false);
          if (text.indexOf('-') > 0) text = text.split('-')[1];
          obj.mode = (obj.mode || []).filter(key => key !== 'Unauthorized' && key !== 'Authorized');
          obj.mode = (text === 'Unauthorized' || text === 'Authorized') ? [text].concat(obj.mode) : [...obj.mode];
        }
        return obj;
      });
    });
  }

  function loadVlanListColor(networksRef, list) {
    list = list || $sakura.deepCopy(vlanListGlobal.value);
    if(!Array.isArray(list)) return
    list.filter(item => item.vlans.find(obj => obj.static)).forEach(item => {
      networksRef.value?.updateIterator(obj => {
        const one = item.vlans.find(nos => nos.ports?.untagged?.includes(obj.portNum));
        if (one) {
          const untagged = one.ports.untagged || [];
          if (untagged.length && item.color) obj.hex = item.color;
        }
        return obj;
      });
    });
  }

  function loadMulticastModeInfo(networksRef) {
    if (modePortListGlobal.value?.physicalPort) {
      modePortListGlobal.value.physicalPort.forEach(item => {
        networksRef.value?.updateIterator(obj => {
          if (item.portNum == obj.portNum) {
            if (item.multicastMode === 2) obj.mode = ['blockMulticast'].concat(obj.mode || []);
            else if (item.multicastMode === 1) obj.mode = ['forceMulticast'].concat(obj.mode || []);
          }
          return obj;
        });
      });
    }
  }

  function loadManualTrunkInfo(lag = [], networksRef) {
    const { physicalPort, lagPort } = manualTrunkGlobal.value || {};
    if (physicalPort) {
      physicalPort.forEach(key => {
        networksRef.value?.updateIterator(obj => {
          if (key === obj.portNum) {
            obj.mode = ['manualTrunk'].concat(obj.mode || []);
          }
          return obj;
        });
      });
    }

    lag.forEach(obj => {
      if (lagPort && lagPort.includes(obj.groupId)) {
        obj.mode = [...(obj.mode || []), 'manualTrunk'];
      }
      if (obj.members.length) {
        obj.members.forEach(key => {
           networksRef.value?.updateIterator(item => {
            if (item.portNum === key) {
              item.hex = obj.hex;
            }
            return item;
          });
        })
      }
    });
  }


  function initLagList() {
    if(!deviceStore.lagList || deviceStore.lagList.length === 0 ) {
      return deviceStore.queryLinkAggregation({ lag_group: 'all' });
    }
    return lagList.value;
  }

  async function initRadius() {
    if (!securityStore.radiusPortConfig || securityStore.radiusPortConfig.length === 0) {
      await securityStore.queryRadiusPortConfig();
    }
    return radiusPortConfig.value;
  }

  async function initManualTrunkGlobal(loop) {
    if(!networksStore.switchConfigManualTrunk || Object.keys(networksStore.switchConfigManualTrunk).length === 0) {
      await networksStore.queryManualTrunkInfo({ loop }).then(json => {
        if (json?.resp?.respCode === 0) {
          // manualTrunkGlobal.value = json.switchConfigManualTrunk;
        }
      });
    }
    // manualTrunkGlobal.value = switchConfigManualTrunk.value;
  }

  async function initVlanListGlobal() {
    if(!networksStore.vlanList || networksStore.vlanList.length === 0) {
      networksStore.queryNetworksList({});
    }
    // vlanListGlobal.value = vlanList.value;
  }

  async function globalQueryPortList() {
    if (!networksStore.portList || networksStore.portList.length === 0) {
      await networksStore.queryPortList({ portid: 'all' });
    }
    return portList.value;
  }

  function globalMulticastModeInfo() {
    if(!multicastStore.isMulticastModePortListUpdated){
        return multicastStore.queryMulticastModeInfo();
    }
  }

  async function globalConfiguration() {
    if(!configurationStore.globalConfigurationList || configurationStore.globalConfigurationList.length === 0) {
      await configurationStore.queryConfiguration({ indexPage: 1, pageSize: 9999 });
    }
    return globalConfigurationList.value;

    // return configurationStore.queryConfiguration({ indexPage: 1, pageSize: 9999 }).then(json => {
    //   if (json.resp.respCode === 0 && json.switchPortStatus.rows.length) {
    //     globalConfigurationList.value = json.switchPortStatus.rows;
    //   } else {
    //     $sakura.sakuraMessage(json.resp.respCode);
    //   }
    // });
  }

  async function globalNeighborList() {
    if(!neighborStore.neighborListUpdated) {
      await neighborStore.queryNeighbor({ indexPage: 1, pageSize: 99999 });
    }
    return neighborList.value;
  }
  function keydownHandle(event) {
    const key = event.key;
    const isNumberKey =
      (event.keyCode >= 48 && event.keyCode <= 57) || // top row 0–9
      (event.keyCode >= 96 && event.keyCode <= 105);  // numpad 0–9
    const isBackspace = event.keyCode === 8;

    if (!isNumberKey && !isBackspace) {
      event.preventDefault();
    }
  }

  onMounted(async() => {
    initLagList();
    initRadius();
    initManualTrunkGlobal();
    initVlanListGlobal();
    await globalQueryPortList();
    globalMulticastModeInfo();
    if(!configurationStore.globalConfigurationList.length){
      globalConfiguration();
    }
    globalNeighborList();
  });

  return {
    portAuthenticationStatus,
    vlanListGlobal,
    manualTrunkGlobal,
    modePortListGlobal,
    globalConfigurationList,
    lagList,
    radiusPortConfig,
    calcLagList,
    networkPriority,
    loadLag,
    loadRadius,
    loadVlanListColor,
    loadMulticastModeInfo,
    loadManualTrunkInfo,
    initLagList,
    initRadius,
    initManualTrunkGlobal,
    initVlanListGlobal,
    globalQueryPortList,
    globalMulticastModeInfo,
    globalConfiguration,
    globalNeighborList,
    keydownHandle
  };
}
